import { CYBERSCORE_ALGORITHM, THREATS, type ScoreBand, type Threat } from '@cyberscore/contracts';
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  FileText,
  Printer,
  Route as RouteIcon,
  Share2,
  ShieldHalf,
  Target,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { industryLabel } from '../assessments/AssessmentsPage';
import { bandLabel, bandRange } from '../portfolio/format';
import { BAND_COLOR, Caps, resTone } from './charts';
import { useBusiness, useBusinessScore } from './context';
import { CardHead, MissionIcon, controlLink, shortDate } from './parts';
import { VERIFIED_AT } from './progress';
import { ShareScore } from './ShareScore';
import { NoAssessment, ScoreProblem } from './states';

/** What each band says about a business, in plain words. */
const BAND_MEANING: Record<ScoreBand, string> = {
  POOR: 'Important protections are missing. A common attack would likely succeed and be expensive to recover from.',
  FAIR: 'The basics are partly in place. A few gaps leave the business exposed to the attacks seen most.',
  GOOD: 'Solid basics. Closing the remaining gaps would make the business a hard target.',
  VERY_GOOD: 'Well protected against the attacks seen most, with only a few improvements left.',
  EXCELLENT: 'Strong, well-evidenced protection across the board.',
};

/**
 * The Cyber Check's feedback (decision 0034): one page a business owner can read, print or hand to their
 * IT provider. The score and what it means, what holds it down, how resilient the business is, what is
 * already in place, and the fixes and plan that raise it. Every number comes from the stored score.
 */
export function ReportPage() {
  const { tenant } = useSession();
  const { loading } = useBusiness();
  const { current: a, view, derived } = useBusinessScore();
  const [sharing, setSharing] = useState(false);
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  if (view.isPending) return <div className="empty">Loading…</div>;
  if (view.isError || !derived) return <ScoreProblem a={a} error={view.error} />;

  const { score: r, explanation: trace, history } = view.data;
  const { missions, plan, next, requirements } = derived;
  const confidence = Math.round(r.controls.evidence_confidence * 100);
  const failed = requirements.filter((q) => q.state === 'failed');
  const unsure = requirements.filter((q) => q.state === 'open');
  const strengths = missions.filter((m) => m.controls > 0 && m.in_place / m.controls >= 0.6).sort((x, y) => y.in_place / y.controls - x.in_place / x.controls); // prettier-ignore
  const end = plan.phases[plan.phases.length - 1]?.score ?? r.score;
  const threats = (Object.keys(THREATS) as Threat[]).sort((x, y) => r.resilience[x] - r.resilience[y]);
  const weakest = threats[0]!;
  const date = history[0]?.created_at ?? a.updated_at;
  const admin = can(tenant, 'people.create');

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Your CyberScore report</h1>
          <p>
            {a.customer_name} · {industryLabel(a.context.industry)} · {a.title} · {shortDate(date)}
          </p>
        </div>
        <div className="head-actions">
          <button type="button" className="bbtn" onClick={() => window.print()}>
            <Printer size={15} /> Print or save as PDF
          </button>
          {!r.provisional && (
            <button type="button" className="bbtn dark" onClick={() => setSharing(true)}>
              <Share2 size={15} /> Share my score
            </button>
          )}
        </div>
      </div>
      {sharing && (
        <ShareScore
          a={a}
          score={{ score: r.score, band: r.band, verified: r.controls.evidence_confidence, provisional: r.provisional, date }}
          onClose={() => setSharing(false)}
        />
      )}

      {r.provisional && (
        <section className="bcard warnbg report-note">
          <AlertTriangle size={18} />
          <span className="grow">
            This report is provisional: some required questions are still open, so the score may move. <a className="link" href={href.assessment(a.id)}>Finish the Cyber Check</a> for a final one.
          </span>
        </section>
      )}

      <div className="bgrid g-side report">
        <section className="bcard">
          <CardHead icon={<FileText size={18} />} title="Summary" sub={`CyberScore model ${CYBERSCORE_ALGORITHM.replace('cyberscore/', '')}`} />
          <div className="report-score">
            <div>
              <div className="big num" style={{ color: BAND_COLOR[r.band] }}>{r.score}</div>
              <div className="muted small">out of 1000</div>
            </div>
            <div className="grow">
              <div className="band" style={{ color: BAND_COLOR[r.band] }}>{bandLabel(r.band).toUpperCase()} <span className="muted small">({bandRange(r.band)}){r.provisional ? ' · provisional' : ''}</span></div>
              <p className="mt-xs">{r.provisional ? 'Based on the answers so far: it will move as you answer the rest. ' : ''}{BAND_MEANING[r.band]}</p>
            </div>
          </div>
          <dl className="kv mt">
            <div><dt>Protections answered</dt><dd className="num">{r.controls.answered} of {r.controls.applicable}</dd></div>
            <div><dt>Backed by evidence</dt><dd className="num">{confidence}% {r.controls.evidence_confidence >= VERIFIED_AT ? '· verified' : '· mostly self-declared'}</dd></div>
            <div><dt>Insurer minimums met</dt><dd className="num">{requirements.length - failed.length - unsure.length} of {requirements.length}</dd></div>
            <div><dt>Score cap</dt><dd className="num">{r.kill_switches.cap === null ? 'None' : `${r.kill_switches.cap}, until the missing minimums are in place`}</dd></div>
            <div><dt>Weakest against</dt><dd>{THREATS[weakest].name} ({Math.round(r.resilience[weakest])})</dd></div>
            <div><dt>In 90 days, with the plan</dt><dd className="num">{end}</dd></div>
          </dl>
        </section>

        <section className="bcard">
          <CardHead icon={<ShieldHalf size={18} />} title="How resilient you are" sub="Against the losses insurers see most, out of 100" />
          <div className="stack-s res-list">
            {threats.map((t) => (
              <div key={t}>
                <div className="row-between small">
                  <span>{THREATS[t].name}</span>
                  <b className="num">{Math.round(r.resilience[t])}</b>
                </div>
                <div className="mt-s"><Caps value={r.resilience[t]} n={22} tone={resTone(r.resilience[t])} /></div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={`bcard mt-l${failed.length + unsure.length ? '' : ' okbg'}`}>
        <CardHead
          tone={failed.length ? 'bad' : unsure.length ? 'warn' : 'ok'}
          icon={failed.length + unsure.length ? <AlertTriangle size={18} /> : <BadgeCheck size={18} />}
          title={failed.length ? 'What holds your score down' : unsure.length ? 'Insurer minimums to confirm' : 'Every insurer minimum is in place'}
          sub="Protections insurers insist on: each one missing caps the score, whatever else is in place"
        />
        {failed.length + unsure.length ? (
          <ul className="report-list">
            {[...failed, ...unsure].map((q) => (
              <li key={q.control}>
                <span className={q.state === 'failed' ? 'bad-t' : 'warn-t'}>{q.state === 'failed' ? 'Missing' : 'Not confirmed'}</span>
                <span className="grow">{q.name}</span>
                {q.points > 0 && <b className="num ok-t">+{q.points}</b>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Keep them in place, and keep the evidence current.</p>
        )}
      </section>

      <section className="bcard mt-l">
        <CardHead tone="ok" icon={<Target size={18} />} title="Fix these first" sub={next.steps.length ? `Together they take your score from ${r.score} to ${next.score}` : 'Nothing left that would add points'} />
        <ol className="report-list numbered">
          {next.steps.map((s) => (
            <li key={s.control}>
              <span className="grow">
                <a href={controlLink(a, trace, s.control, s.mission).to}>{s.name}</a>
                {s.lifts_cap && <span className="bad-t small"> · lifts the cap</span>}
              </span>
              <b className="num ok-t">+{s.points}</b>
            </li>
          ))}
        </ol>
      </section>

      <div className="bgrid g-2 mt-l">
        <section className="bcard">
          <CardHead icon={<RouteIcon size={18} />} title="Your 90-day plan" sub={`${r.score} today, ${end} once every step is done`} />
          {plan.phases.map((p) => (
            <div key={p.name} className="report-phase">
              <div className="row-between"><b>{p.name}</b><span className="num ok-t">+{p.points} · {p.score}</span></div>
              <div className="muted small">{p.steps.map((s) => s.name).join(' · ')}</div>
            </div>
          ))}
          <a className="link-btn mt" href={href.plan()}>The full plan <ArrowRight size={14} /></a>
        </section>
        <section className="bcard">
          <CardHead tone="ok" icon={<CheckCircle2 size={18} />} title="What you already do well" sub="Missions with most of their protections in place" />
          {strengths.length ? (
            <ul className="report-list">
              {strengths.map((m) => (
                <li key={m.id}>
                  <MissionIcon id={m.id} size={15} />
                  <span className="grow">{m.name}</span>
                  <span className="num muted">{m.in_place}/{m.controls}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No mission is mostly in place yet. The fixes above are the quickest way to start.</p>
          )}
        </section>
      </div>

      <section className="bcard soft mt-l report-next">
        <b>What to do next</b>
        <ul>
          <li><a href={href.plan()}>Work through the plan</a>, or hand this report to your IT provider.</li>
          <li><a href={href.locker()}>Upload evidence</a> for what you have in place: verified answers count for more{confidence < VERIFIED_AT * 100 ? `, and you’re at ${confidence}% today` : ''}.</li>
          <li><a href={href.connections()}>Connect Microsoft 365</a> to check settings automatically.</li>
          {admin && <li><a href={href.people()}><Users size={13} /> Invite your team</a> to answer the parts they know best.</li>}
          {!r.provisional && <li>Share your score with insurers, customers and suppliers when you choose.</li>}
        </ul>
        <p className="muted small">CyberScore estimates protection from your answers and evidence. It is not a guarantee against attack; insurers set their own terms.</p>
      </section>
    </>
  );
} // prettier-ignore
